import { Grid } from "./grid";
import { Tile } from "./tile";
import type { Rng } from "./rng";
import type {
  Direction,
  MoveResult,
  Position,
  SerializedState,
  Vector,
} from "./types";

const WINNING_VALUE = 2048;

const VECTORS: Record<Direction, Vector> = {
  0: { x: 0, y: -1 }, // up
  1: { x: 1, y: 0 }, // right
  2: { x: 0, y: 1 }, // down
  3: { x: -1, y: 0 }, // left
};

/** The movement vector for a direction. */
export function getVector(direction: Direction): Vector {
  return VECTORS[direction];
}

export function positionsEqual(a: Position, b: Position): boolean {
  return a.x === b.x && a.y === b.y;
}

/**
 * The pure 2048 engine: holds the board + score and applies moves. DOM-free and
 * deterministic given its injected {@link Rng}, so the same engine runs in the
 * browser (rendering from `grid`) and on the server (replaying a move list).
 *
 * Ported from the original `GameManager`, with IO (input, rendering, storage,
 * analytics) removed and randomness routed through the RNG.
 */
export class Game {
  readonly size: number;
  grid: Grid;
  score = 0;
  over = false;
  won = false;
  keepPlaying = false;
  maxTile = 0;

  private readonly rng: Rng;
  private readonly startTiles = 2;

  constructor(rng: Rng, size = 4, previous?: SerializedState) {
    this.rng = rng;
    this.size = size;

    if (previous) {
      this.grid = new Grid(size, previous.grid.cells);
      this.score = previous.score;
      this.over = previous.over;
      this.won = previous.won;
      this.keepPlaying = previous.keepPlaying;
    } else {
      this.grid = new Grid(size);
      this.addStartTiles();
    }

    this.maxTile = this.computeMaxTile();
  }

  /** Whether input should be ignored (lost, or won and not continuing). */
  isTerminated(): boolean {
    return this.over || (this.won && !this.keepPlaying);
  }

  /** Continue playing after reaching 2048. */
  continuePlaying(): void {
    this.keepPlaying = true;
  }

  serialize(): SerializedState {
    return {
      grid: this.grid.serialize(),
      score: this.score,
      over: this.over,
      won: this.won,
      keepPlaying: this.keepPlaying,
    };
  }

  /** Apply a move in the given direction; returns what happened. */
  move(direction: Direction): MoveResult {
    if (this.isTerminated()) {
      return this.idleResult();
    }

    const vector = getVector(direction);
    const traversals = this.buildTraversals(vector);
    const wasWon = this.won;
    let moved = false;
    let gained = 0;

    this.prepareTiles();

    for (const x of traversals.x) {
      for (const y of traversals.y) {
        const cell: Position = { x, y };
        const tile = this.grid.cellContent(cell);
        if (!tile) continue;

        const positions = this.findFarthestPosition(cell, vector);
        const next = this.grid.cellContent(positions.next);

        if (next && next.value === tile.value && !next.mergedFrom) {
          // Merge `tile` into `next`.
          const merged = new Tile(positions.next, tile.value * 2);
          merged.mergedFrom = [tile, next];

          this.grid.insertTile(merged);
          this.grid.removeTile(tile);
          tile.updatePosition(positions.next);

          this.score += merged.value;
          gained += merged.value;
          if (merged.value > this.maxTile) this.maxTile = merged.value;
          if (merged.value === WINNING_VALUE) this.won = true;
        } else {
          this.moveTile(tile, positions.farthest);
        }

        if (!positionsEqual(cell, { x: tile.x, y: tile.y })) {
          moved = true;
        }
      }
    }

    if (moved) {
      this.addRandomTile();
      if (!this.movesAvailable()) {
        this.over = true;
      }
    }

    return {
      moved,
      score: this.score,
      gained,
      maxTile: this.maxTile,
      won: this.won,
      justWon: this.won && !wasWon,
      over: this.over,
    };
  }

  private idleResult(): MoveResult {
    return {
      moved: false,
      score: this.score,
      gained: 0,
      maxTile: this.maxTile,
      won: this.won,
      justWon: false,
      over: this.over,
    };
  }

  private addStartTiles(): void {
    for (let i = 0; i < this.startTiles; i++) {
      this.addRandomTile();
    }
  }

  /** Spawn a tile in a random empty cell: value draw first, then position. */
  private addRandomTile(): void {
    if (!this.grid.cellsAvailable()) return;
    const value = this.rng.next() < 0.9 ? 2 : 4;
    const cell = this.grid.randomAvailableCell(this.rng);
    if (cell) {
      this.grid.insertTile(new Tile(cell, value));
      if (value > this.maxTile) this.maxTile = value;
    }
  }

  private prepareTiles(): void {
    this.grid.eachCell((_x, _y, tile) => {
      if (tile) {
        tile.mergedFrom = null;
        tile.savePosition();
      }
    });
  }

  private moveTile(tile: Tile, cell: Position): void {
    this.grid.removeTile(tile);
    tile.updatePosition(cell);
    this.grid.insertTile(tile);
  }

  private buildTraversals(vector: Vector): { x: number[]; y: number[] } {
    const traversals: { x: number[]; y: number[] } = { x: [], y: [] };
    for (let pos = 0; pos < this.size; pos++) {
      traversals.x.push(pos);
      traversals.y.push(pos);
    }
    // Always traverse from the farthest cell in the chosen direction.
    if (vector.x === 1) traversals.x.reverse();
    if (vector.y === 1) traversals.y.reverse();
    return traversals;
  }

  private findFarthestPosition(
    cell: Position,
    vector: Vector,
  ): { farthest: Position; next: Position } {
    let previous: Position;
    let current: Position = cell;
    do {
      previous = current;
      current = { x: previous.x + vector.x, y: previous.y + vector.y };
    } while (
      this.grid.withinBounds(current) &&
      this.grid.cellAvailable(current)
    );
    return { farthest: previous, next: current };
  }

  private movesAvailable(): boolean {
    return this.grid.cellsAvailable() || this.tileMatchesAvailable();
  }

  private tileMatchesAvailable(): boolean {
    for (let x = 0; x < this.size; x++) {
      for (let y = 0; y < this.size; y++) {
        const tile = this.grid.cellContent({ x, y });
        if (!tile) continue;
        for (const direction of [0, 1, 2, 3] as Direction[]) {
          const vector = getVector(direction);
          const other = this.grid.cellContent({
            x: x + vector.x,
            y: y + vector.y,
          });
          if (other && other.value === tile.value) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private computeMaxTile(): number {
    let max = 0;
    this.grid.eachCell((_x, _y, tile) => {
      if (tile && tile.value > max) max = tile.value;
    });
    return max;
  }
}

/** Convenience constructor for a fresh game. */
export function newGame(rng: Rng, size = 4): Game {
  return new Game(rng, size);
}
